<template>
  <Teleport to="body">
    <div v-if="show" class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" @click.self="closeModal" style="margin: 0;">
      <div class="relative w-full max-w-md bg-brand-dark-lighter rounded-2xl border border-pyth-purple/30 shadow-2xl overflow-hidden">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-pyth-purple/20">
        <h2 class="text-xl font-bold text-white">Connect Wallet</h2>
        <button
          @click="closeModal"
          class="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-brand-dark"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Wallet List -->
      <div class="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
        <button
          v-for="wallet in availableWallets"
          :key="wallet.adapter.name"
          @click="handleWalletSelect(wallet)"
          :disabled="connecting"
          class="wallet-option w-full flex items-center gap-4 p-4 bg-brand-dark rounded-xl border border-pyth-purple/20 hover:border-pyth-purple hover:bg-brand-dark-lighter transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
        >
          <!-- Wallet Icon -->
          <div class="flex-shrink-0">
            <img
              v-if="wallet.adapter.icon"
              :src="wallet.adapter.icon"
              :alt="wallet.adapter.name"
              class="w-10 h-10 rounded-lg"
            />
            <div v-else class="w-10 h-10 bg-pyth-purple/20 rounded-lg flex items-center justify-center">
              <svg class="w-6 h-6 text-pyth-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 019-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
            </div>
          </div>

          <!-- Wallet Info -->
          <div class="flex-1 text-left">
            <div class="font-semibold text-white group-hover:text-pyth-cyan transition-colors">
              {{ wallet.adapter.name }}
            </div>
            <div class="text-sm text-gray-400">
              {{ wallet.readyState === 'Installed' ? 'Detected' : 'Not Installed' }}
            </div>
          </div>

          <!-- Status Indicator -->
          <div class="flex-shrink-0">
            <div v-if="wallet.readyState === 'Installed'" class="w-2 h-2 bg-green-500 rounded-full"></div>
            <svg v-else class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>

        <!-- Empty State -->
        <div v-if="availableWallets.length === 0" class="text-center py-8">
          <div class="text-4xl mb-4">dY`></div>
          <p class="text-gray-400">No wallets detected</p>
          <p class="text-sm text-gray-500 mt-2">Please install a Solana wallet extension</p>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 bg-brand-dark/50 border-t border-pyth-purple/20">
        <p class="text-xs text-gray-500 text-center">
          New to Solana?
          <a
            href="https://phantom.app"
            target="_blank"
            class="text-pyth-cyan hover:underline ml-1"
          >
            Get Phantom Wallet
          </a>
        </p>
      </div>

      <!-- Loading Overlay -->
      <div v-if="connecting" class="absolute inset-0 bg-brand-dark/80 backdrop-blur-sm flex items-center justify-center">
        <div class="text-center">
          <div class="animate-spin text-4xl mb-4">💜</div>
          <p class="text-white font-medium">Connecting...</p>
        </div>
      </div>
    </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useWallet } from '@solana/wallet-adapter-vue'
import { WalletReadyState } from '@solana/wallet-adapter-base'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'walletSelected'])

const walletContext = useWallet()

const { wallets, select, connect, connecting } = walletContext

// Filter wallets to show installed ones first
const availableWallets = computed(() => {
  // wallets is already an array, not a ref!
  if (!wallets || !Array.isArray(wallets)) {
    return []
  }

  return wallets
    .map(wallet => ({
      adapter: wallet,
      readyState: wallet.readyState
    }))
    .sort((a, b) => {
      // Installed wallets first
      if (a.readyState === WalletReadyState.Installed && b.readyState !== WalletReadyState.Installed) {
        return -1
      }
      if (b.readyState === WalletReadyState.Installed && a.readyState !== WalletReadyState.Installed) {
        return 1
      }
      return 0
    })
})

async function handleWalletSelect(selectedWallet) {
  try {
    // Find the matching wallet from the context
    const walletToSelect = wallets.find(w => w.name === selectedWallet.adapter.name)

    if (!walletToSelect) {
      throw new Error(`Wallet ${selectedWallet.adapter.name} not found in context`)
    }

    // IMPORTANT: This will prompt user to UNLOCK wallet if locked
    await walletToSelect.connect()

    // Verify wallet is actually unlocked by checking publicKey
    if (!walletToSelect.publicKey) {
      throw new Error('Wallet connection failed - no public key (wallet may be locked)')
    }

    // Set up disconnect listener to detect when wallet locks/disconnects
    if (walletToSelect.on) {
      walletToSelect.on('disconnect', () => {
        // This will be handled by the wallet adapter watch in useWalletConnection
      })

      walletToSelect.on('accountChanged', (publicKey) => {
        // App should handle account changes
      })
    }

    // Emit success event with the public key AND wallet adapter (for signature)
    emit('walletSelected', {
      name: selectedWallet.adapter.name,
      publicKey: walletToSelect.publicKey,
      wallet: walletToSelect // Pass wallet adapter for signature
    })

    // Close modal
    closeModal()
  } catch (error) {
    // Show user-friendly error message
    if (error.message?.includes('locked')) {
      alert('Please unlock your wallet and try again')
    }

    // If wallet is not installed, open install page
    if (selectedWallet.readyState !== WalletReadyState.Installed && selectedWallet.adapter.url) {
      window.open(selectedWallet.adapter.url, '_blank')
    }
  }
}

function closeModal() {
  if (!connecting.value) {
    emit('close')
  }
}

// Close on Escape key
onMounted(() => {
  const handleEscape = (e) => {
    if (e.key === 'Escape' && props.show) {
      closeModal()
    }
  }
  window.addEventListener('keydown', handleEscape)

  return () => {
    window.removeEventListener('keydown', handleEscape)
  }
})
</script>

<style scoped>
.wallet-option {
  cursor: pointer;
}

.wallet-option:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(130, 71, 229, 0.2);
}

.wallet-option:active {
  transform: translateY(0);
}

/* Scrollbar styling */
.max-h-\[60vh\]::-webkit-scrollbar {
  width: 8px;
}

.max-h-\[60vh\]::-webkit-scrollbar-track {
  background: rgba(130, 71, 229, 0.1);
  border-radius: 4px;
}

.max-h-\[60vh\]::-webkit-scrollbar-thumb {
  background: rgba(130, 71, 229, 0.3);
  border-radius: 4px;
}

.max-h-\[60vh\]::-webkit-scrollbar-thumb:hover {
  background: rgba(130, 71, 229, 0.5);
}
</style>
